import React, { useState, useEffect } from 'react';
import {
  Layout,
  Menu,
  Card,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Statistic,
  Table,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Drawer,
  Avatar,
  Tag,
  Popconfirm,
  DatePicker,
  Divider
} from 'antd';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

// Configurar plugins do dayjs
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
import {
  UserOutlined,
  BankOutlined,
  HistoryOutlined,
  DashboardOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  MenuOutlined,
  SettingOutlined,
  LogoutOutlined,
  EyeOutlined,
  ReloadOutlined,
  TagOutlined,
  EnvironmentOutlined,
  ApiOutlined,
  FileTextOutlined,
  KeyOutlined,
  CloudServerOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Navbar from '../../components/Navbar';
import { admin } from '../../services/api';

import RotasManagement from './RotasManagement';
import WebhookStatus from './WebhookStatus';
import WebhookTest from './WebhookTest';
import WebhooksManagement from './WebhooksManagement';
import OrgSequence from './OrgSequence';
import VeiculosManagement from './VeiculosManagement';
import LabelTemplatesManagement from './LabelTemplatesManagement';
import PalavrasManagement from './PalavrasManagement';
import IntegracoesDashboard from './IntegracoesDashboard';
import SswIntegracao from './SswIntegracao';
import PortalNfeIntegracao from './PortalNfeIntegracao';
import ApiIntegracoes from './ApiIntegracoes';
import ApiKeyIntegracoes from './ApiKeyIntegracoes';
import './AdminDashboard.css';
import '../../styles/global-input-fixes.css';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const { currentTheme, toggleTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  // Estados para WS-Print
  const [wsStatus, setWsStatus] = useState(null);
  const [wsClients, setWsClients] = useState([]);
  const [wsLoading, setWsLoading] = useState(false);

  // Estados para modais
  const [userModalVisible, setUserModalVisible] = useState(false);
  const [branchModalVisible, setBranchModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingBranch, setEditingBranch] = useState(null);

  // Estados para dados
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({});


  // Estados para filtros
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(null);
  const [selectedRoleFilter, setSelectedRoleFilter] = useState(null);

  // Estados para filtros do histórico de login
  const [dateRange, setDateRange] = useState(null);
  const [filteredLoginHistory, setFilteredLoginHistory] = useState([]);

  // Forms
  const [userForm] = Form.useForm();
  const [branchForm] = Form.useForm();

  // Menu items para sidebar
  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: 'users',
      icon: <UserOutlined />,
      label: 'Usuários',
    },
    {
      key: 'branches',
      icon: <BankOutlined />,
      label: 'Filiais',
    },
    {
      key: 'veiculos',
      icon: <TagOutlined />,
      label: 'Veículos',
    },
    {
      key: 'rotas',
      icon: <EnvironmentOutlined />,
      label: 'Rotas',
    },
    {
      key: 'palavras',
      icon: <FileTextOutlined />,
      label: 'Palavras',
    },
    {
      key: 'integracoes',
      icon: <ApiOutlined />,
      label: 'Integrações',
      children: [
        {
          key: 'integracoes-dashboard',
          icon: <DashboardOutlined />,
          label: 'Dashboard',
        },
        {
          key: 'integracoes-ssw',
          icon: <CloudServerOutlined />,
          label: 'SSW',
        },
        {
          key: 'integracoes-portal-nfe',
          icon: <FileTextOutlined />,
          label: 'Portal Nf-e',
        },
        {
          key: 'integracoes-api',
          icon: <ApiOutlined />,
          label: 'API',
        },
        {
          key: 'integracoes-api-key',
          icon: <KeyOutlined />,
          label: 'API KEY',
        },
      ],
    },
    {
      key: 'webhooks',
      icon: <ApiOutlined />,
      label: 'WebHooks',
    },
    {
      key: 'org-sequence',
      icon: <SettingOutlined />,
      label: 'Sequência NR1',
    },
    /*
    {
      key: 'label-templates',
      icon: <TagOutlined />,
      label: 'Modelos de Etiqueta',
    },
    */
    {
      key: 'ws-print',
      icon: <ApiOutlined />,
      label: 'WS-Print',
    },
    {
      key: 'login-history',
      icon: <HistoryOutlined />,
      label: 'Histórico de Login',
    },
  ];

  // Carregar dados iniciais
  useEffect(() => {
    loadDashboardData();
    loadUsers();
    loadBranches();
    loadLoginHistory();
    refreshWsInfo();
  }, []);

  // Carregar dados do WS-Print quando a seção estiver ativa
  useEffect(() => {
    if (activeSection === 'ws-print') {
      refreshWsInfo();
    }
  }, [activeSection]);

  const refreshWsInfo = async () => {
    setWsLoading(true);
    try {
      const [statusResp, clientsResp] = await Promise.all([
        admin.getWsStatus(),
        admin.getWsClients(),
      ]);
      setWsStatus(statusResp);
      setWsClients(clientsResp?.clients || []);
    } catch (error) {
      console.error('Erro ao carregar dados do WS-Print:', error);
      message.error(error.message || 'Erro ao carregar dados do WS-Print');
    } finally {
      setWsLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      const stats = await admin.getDashboardStats();
      setDashboardStats(stats);
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      message.error('Erro ao carregar dados do dashboard');
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const usersData = await admin.getUsers();
      // Transformar dados para o formato esperado pelo componente
      const formattedUsers = usersData.map(user => ({
        id: user.id,
        codigo: user.codigo,
        name: user.name,

        roles: user.roles?.map(r => r.role?.name) || [],
        active: user.isActive,
        lastLogin: user.updatedAt,
        branch: user.assignedBranch?.name || 'Não atribuída',
        assignedBranchId: user.assignedBranchId
      }));
      setUsers(formattedUsers);
      setFilteredUsers(formattedUsers);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
      message.error('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  // Função para aplicar filtros
  const applyFilters = () => {
    let filtered = [...users];

    // Filtro por filial
    if (selectedBranchFilter) {
      filtered = filtered.filter(user => user.branch === selectedBranchFilter);
    }

    // Filtro por role
    if (selectedRoleFilter) {
      filtered = filtered.filter(user => user.roles.includes(selectedRoleFilter));
    }

    setFilteredUsers(filtered);
  };

  // Função para aplicar filtros do histórico de login
  const applyLoginHistoryFilters = () => {
    let filtered = [...loginHistory];

    // Filtro por data
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      const [startDate, endDate] = dateRange;

      filtered = filtered.filter(entry => {
        try {
          // Extrair apenas a data (sem horário) do loginTime
          const entryDateStr = entry.loginTime.split(' ')[0]; // "DD/MM/YYYY"
          const [day, month, year] = entryDateStr.split('/');

          // Criar dayjs object da data da entrada usando o formato DD/MM/YYYY
          const entryDayjs = dayjs(`${day}/${month}/${year}`, 'DD/MM/YYYY');

          // Comparar usando apenas a data (sem horário)
          const startDateOnly = startDate.startOf('day');
          const endDateOnly = endDate.endOf('day');
          const entryDateOnly = entryDayjs.startOf('day');

          return entryDateOnly.isSameOrAfter(startDateOnly) && entryDateOnly.isSameOrBefore(endDateOnly);
        } catch (error) {
          console.error('Erro ao processar data:', error, entry.loginTime);
          return false;
        }
      });
    }

    setFilteredLoginHistory(filtered);
  };

  // Aplicar filtros quando os filtros mudarem
  useEffect(() => {
    applyFilters();
  }, [selectedBranchFilter, selectedRoleFilter, users]);

  // Aplicar filtros do histórico de login quando mudarem
  useEffect(() => {
    applyLoginHistoryFilters();
  }, [dateRange, loginHistory]);

  const loadBranches = async () => {
    try {
      const branchesData = await admin.getBranches();
      // Transformar dados para o formato esperado pelo componente
      const formattedBranches = branchesData.map(branch => ({
        id: branch.id,
        name: branch.name,
        sigla: branch.sigla,
        codigo: branch.codigo || '',
        active: branch.isActive,
        userCount: branch._count?.users || 0
      }));
      setBranches(formattedBranches);
    } catch (error) {
      console.error('Erro ao carregar filiais:', error);
      message.error('Erro ao carregar filiais');
    }
  };

  const loadLoginHistory = async () => {
    try {
      const historyData = await admin.getLoginHistory({ limit: 100 });
      // Transformar dados para o formato esperado pelo componente
      const formattedHistory = historyData.data.map(entry => ({
        id: entry.id,
        user: entry.user?.name || 'Usuário não encontrado',

        loginTime: new Date(entry.createdAt).toLocaleString('pt-BR'),
        ipAddress: entry.ipAddress || 'N/A',
        userAgent: entry.userAgent || 'N/A',
        success: entry.action === 'LOGIN',
        action: entry.action,
        branch: entry.user?.assignedBranch?.name || 'N/A'
      }));

      setLoginHistory(formattedHistory);
      setFilteredLoginHistory(formattedHistory);
    } catch (error) {
      console.error('Erro ao carregar histórico de login:', error);
      message.error('Erro ao carregar histórico de login');
    }
  };



  // Handlers para usuários
  const handleCreateUser = async () => {
    setEditingUser(null);
    userForm.resetFields();
    try {
      if (!branches || branches.length === 0) {
        await loadBranches();
      }
    } catch (e) {
      console.warn('Falha ao garantir filiais antes de abrir modal de usuário:', e);
    }
    setUserModalVisible(true);
  };

  const handleEditUser = async (user) => {
    setEditingUser(user);

    // Mapear dados do usuário para o formato do formulário
    const formData = {
      codigo: user.codigo,
      name: user.name,

      roles: user.roles || [], // roles já vem como array de strings
      assignedBranchId: user.assignedBranchId, // usar ID da filial
      active: user.active
    };

    userForm.setFieldsValue(formData);
    try {
      if (!branches || branches.length === 0) {
        await loadBranches();
      }
    } catch (e) {
      console.warn('Falha ao garantir filiais antes de editar usuário:', e);
    }
    setUserModalVisible(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await admin.deleteUser(userId);
      setUsers(users.filter(u => u.id !== userId));
      message.success('Usuário excluído com sucesso');
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      message.error(error.message || 'Erro ao excluir usuário');
    }
  };

  const handleUserSubmit = async (values) => {
    try {
      if (editingUser) {
        // Para atualização, enviar apenas campos permitidos pelo UpdateUserDto
        const updateData = {
          name: values.name,
          assignedBranchId: values.assignedBranchId,
          isActive: values.active !== undefined ? values.active : true,
          roles: values.roles
        };

        // Atualizar usuário
        const updatedUser = await admin.updateUser(editingUser.id, updateData);

        // Transformar dados para o formato esperado pelo componente
        const formattedUser = {
          id: updatedUser.id,
          codigo: updatedUser.codigo,
          name: updatedUser.name,

          roles: updatedUser.roles?.map(r => r.role?.name) || [],
          active: updatedUser.isActive,
          lastLogin: updatedUser.updatedAt,
          branch: updatedUser.assignedBranch?.name || 'Não atribuída',
          assignedBranchId: updatedUser.assignedBranchId
        };

        setUsers(users.map(u => u.id === editingUser.id ? formattedUser : u));
        message.success('Usuário atualizado com sucesso');
      } else {
        // Para Criação, usar formato do CreateUserDto
        const createData = {
          codigo: values.codigo,
          name: values.name,
          password: values.password,
          assignedBranchId: values.assignedBranchId,
          isActive: values.active !== undefined ? values.active : true,
          roles: values.roles
        };

        // Criar novo usuário
        const newUser = await admin.createUser(createData);

        // Transformar dados para o formato esperado pelo componente
        const formattedUser = {
          id: newUser.id,
          codigo: newUser.codigo,
          name: newUser.name,

          roles: newUser.roles?.map(r => r.role?.name) || [],
          active: newUser.isActive,
          lastLogin: newUser.updatedAt,
          branch: newUser.assignedBranch?.name || 'Não atribuída',
          assignedBranchId: newUser.assignedBranchId
        };

        setUsers([...users, formattedUser]);
        message.success('Usuário criado com sucesso');
      }
      setUserModalVisible(false);
      userForm.resetFields();
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);

      // Tratar erro específico de código duplicado
      if (error.message && error.message.includes('Código já está em uso')) {
        message.warning('Este código já está sendo usado por outro usuário. Por favor, escolha um código diferente.');
      } else {
        message.error(error.message || 'Erro ao salvar usuário');
      }
    }
  };

  // Handlers para filiais
  const handleCreateBranch = () => {
    setEditingBranch(null);
    branchForm.resetFields();
    setBranchModalVisible(true);
  };

  const handleEditBranch = (branch) => {
    setEditingBranch(branch);
    branchForm.setFieldsValue(branch);
    setBranchModalVisible(true);
  };

  const handleDeleteBranch = async (branchId) => {
    try {
      await admin.deleteBranch(branchId);
      setBranches(branches.filter(b => b.id !== branchId));
      message.success('Filial excluída com sucesso');
    } catch (error) {
      console.error('Erro ao excluir filial:', error);
      message.error(error.message || 'Erro ao excluir filial');
    }
  };

  const handleBranchSubmit = async (values) => {
    try {
      // Transformar dados para o formato da API
      const branchData = {
        name: values.name,
        sigla: values.acronym,
        isActive: values.active !== undefined ? values.active : true
      };

      // Enviar `codigo` apenas se preenchido (evita erro de propriedade desconhecida)
      if (values.codigo && values.codigo.trim().length > 0) {
        branchData.codigo = values.codigo.trim();
      }

      if (editingBranch) {
        const updatedBranch = await admin.updateBranch(editingBranch.id, branchData);
        // Atualizar na lista local
        const formattedBranch = {
          id: updatedBranch.id,
          name: updatedBranch.name,
          acronym: updatedBranch.sigla,
          codigo: updatedBranch.codigo || '',
          active: updatedBranch.isActive,
          userCount: updatedBranch._count?.users || 0
        };
        setBranches(branches.map(b => b.id === editingBranch.id ? formattedBranch : b));
        message.success('Filial atualizada com sucesso');
      } else {
        const newBranch = await admin.createBranch(branchData);
        // Adicionar à lista local
        const formattedBranch = {
          id: newBranch.id,
          name: newBranch.name,
          acronym: newBranch.sigla,
          codigo: newBranch.codigo || '',
          active: newBranch.isActive,
          userCount: newBranch._count?.users || 0
        };
        setBranches([...branches, formattedBranch]);
        message.success('Filial criada com sucesso');
      }
      setBranchModalVisible(false);
      branchForm.resetFields();
    } catch (error) {
      console.error('Erro ao salvar filial:', error);
      message.error(error.message || 'Erro ao salvar filial');
    }
  };

  // Colunas das tabelas
  const userColumns = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      width: 100,
      sorter: (a, b) => (a.codigo || '').localeCompare(b.codigo || ''),
      render: (codigo) => (
        <Tag color="blue">{codigo || 'N/A'}</Tag>
      ),
    },
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} />
          <div>
            <div>{text}</div>

          </div>
        </Space>
      ),
    },
    {
      title: 'Roles',
      dataIndex: 'roles',
      key: 'roles',
      sorter: (a, b) => a.roles.join(',').localeCompare(b.roles.join(',')),
      render: (roles) => (
        <Space>
          {roles.map(role => (
            <Tag key={role} color="blue">{role}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Filial',
      dataIndex: 'branch',
      key: 'branch',
      sorter: (a, b) => a.branch.localeCompare(b.branch),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      sorter: (a, b) => a.active - b.active,
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Ativo' : 'Inativo'}
        </Tag>
      ),
    },
    {
      title: 'Último Login',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      sorter: (a, b) => {
        if (!a.lastLogin && !b.lastLogin) return 0;
        if (!a.lastLogin) return 1;
        if (!b.lastLogin) return -1;
        return new Date(a.lastLogin) - new Date(b.lastLogin);
      },
      render: (date) => date ? new Date(date).toLocaleString('pt-BR') : 'Nunca'
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditUser(record)}
          />
          <Popconfirm
            title="Tem certeza que deseja excluir este usuário?"
            onConfirm={() => handleDeleteUser(record.id)}
            okText="Sim"
            cancelText="Não"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const branchColumns = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      width: 120,
      render: (codigo) => (
        <Tag color="blue">{codigo || 'N/A'}</Tag>
      ),
    },
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Sigla',
      dataIndex: 'acronym',
      key: 'acronym',
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Ativa' : 'Inativa'}
        </Tag>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditBranch(record)}
          />
          <Popconfirm
            title="Tem certeza que deseja excluir esta filial?"
            onConfirm={() => handleDeleteBranch(record.id)}
            okText="Sim"
            cancelText="Não"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const loginHistoryColumns = [
    {
      title: 'Usuário',
      dataIndex: 'user',
      key: 'user',
      sorter: (a, b) => a.user.localeCompare(b.user),
      render: (text, record) => (
        <div>
          <div>{text}</div>

        </div>
      ),
    },
    {
      title: 'Data/Hora',
      dataIndex: 'loginTime',
      key: 'loginTime',
      sorter: (a, b) => {
        // Converter DD/MM/YYYY HH:mm:ss para Date para comparação
        const dateA = new Date(a.loginTime.split(' ')[0].split('/').reverse().join('-') + ' ' + a.loginTime.split(' ')[1]);
        const dateB = new Date(b.loginTime.split(' ')[0].split('/').reverse().join('-') + ' ' + b.loginTime.split(' ')[1]);
        return dateA - dateB;
      },
    },
    {
      title: 'IP',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      sorter: (a, b) => a.ipAddress.localeCompare(b.ipAddress),
    },
    {
      title: 'Navegador',
      dataIndex: 'userAgent',
      key: 'userAgent',
      sorter: (a, b) => a.userAgent.localeCompare(b.userAgent),
      render: (text) => text.split(' ')[0]
    },
    {
      title: 'Status',
      dataIndex: 'success',
      key: 'success',
      sorter: (a, b) => a.success - b.success,
      render: (success) => (
        <Tag color={success ? 'green' : 'red'}>
          {success ? 'Sucesso' : 'Falha'}
        </Tag>
      ),
    },
  ];

  // Renderizar conteúdo baseado na seção ativa
  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return (
          <div>
            <Title level={2}>Dashboard Administrativo</Title>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total de Usuários"
                    value={dashboardStats.totalUsers}
                    prefix={<UserOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Filiais Ativas"
                    value={dashboardStats.activeBranches}
                    prefix={<BankOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Logins Hoje"
                    value={dashboardStats.todayLogins}
                    prefix={<HistoryOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Uptime do Sistema"
                    value={dashboardStats.systemUptime}
                    suffix="%"
                  />
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Card title="Últimos Usuários Criados">
                  <Table
                    dataSource={users.slice(0, 5)}
                    columns={userColumns.slice(0, 3)}
                    pagination={false}
                    size="small"
                    scroll={{ x: 'max-content' }}
                    rowKey="id"
                  />
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card title="Últimos Logins" style={{ height: 330 }} bodyStyle={{ padding: 0, overflow: 'hidden' }}>
                  <Table
                    dataSource={loginHistory.slice(0, 10)}
                    columns={loginHistoryColumns.slice(0, 3)}
                    pagination={false}
                    size="small"
                    scroll={{ x: 'max-content', y: 270 }}
                    rowKey="id"
                  />
                </Card>
              </Col>
            </Row>
          </div>
        );

      case 'users':
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Title level={2}>Gerenciamento de Usuários</Title>
              <Space>
                <Button icon={<ReloadOutlined />} onClick={loadUsers}>Atualizar</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateUser}>
                  Novo Usuário
                </Button>
              </Space>
            </div>

            {/* Filtros */}
            <Card style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Text strong>Filtros:</Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Text>Filial:</Text>
                  <Select
                    placeholder="Todas as filiais"
                    style={{ width: 200 }}
                    allowClear
                    value={selectedBranchFilter}
                    onChange={setSelectedBranchFilter}
                  >
                    {[...new Set(users.map(user => user.branch))].filter(branch => branch !== 'Não atribuída').map(branch => (
                      <Option key={branch} value={branch}>{branch}</Option>
                    ))}
                  </Select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Text>Role:</Text>
                  <Select
                    placeholder="Todas as roles"
                    style={{ width: 200 }}
                    allowClear
                    value={selectedRoleFilter}
                    onChange={setSelectedRoleFilter}
                  >
                    {[...new Set(users.flatMap(user => user.roles))].map(role => (
                      <Option key={role} value={role}>{role}</Option>
                    ))}
                  </Select>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Text type="secondary">Total: {filteredUsers.length} usuários</Text>
                </div>
              </div>
            </Card>

            <Card>
              <Table
                dataSource={filteredUsers}
                columns={userColumns}
                loading={loading}
                rowKey="id"
                scroll={{ x: 'max-content' }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `Total: ${total} usuários`
                }}
              />
            </Card>
          </div>
        );

      case 'branches':
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Title level={2}>Gerenciamento de Filiais</Title>
              <Space>
                <Button icon={<ReloadOutlined />} onClick={loadBranches}>Atualizar</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateBranch}>
                  Nova Filial
                </Button>
              </Space>
            </div>
            <Card>
              <Table
                dataSource={branches}
                columns={branchColumns}
                rowKey="id"
                scroll={{ x: 'max-content' }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `Total: ${total} filiais`
                }}
              />
            </Card>
          </div>
        );

      case 'rotas':
        return <RotasManagement />;

      case 'palavras':
        return <PalavrasManagement />;

      case 'integracoes-dashboard':
        return <IntegracoesDashboard />;

      case 'integracoes-ssw':
        return (
          <div>
            <Title level={2}>SSW</Title>
            <SswIntegracao />
          </div>
        );

      case 'integracoes-portal-nfe':
        return (
          <div>
            <Title level={2}>Portal Nf-e</Title>
            <PortalNfeIntegracao />
          </div>
        );

      case 'integracoes-api':
        return (
          <div>
            <Title level={2}>API</Title>
            <ApiIntegracoes />
          </div>
        );

      case 'integracoes-api-key':
        return (
          <div>
            <Title level={2}>API KEY</Title>
            <ApiKeyIntegracoes />
          </div>
        );

      case 'veiculos':
        return (
          <div>
            <Title level={2}>Gerenciamento de Veículos</Title>
            <VeiculosManagement branches={branches} />
          </div>
        );

      case 'webhooks':
        return (
          <div>
            <WebhooksManagement />
            <Divider style={{ margin: '32px 0' }} />
            <Title level={3}>Ferramentas de WebHook</Title>
            <Card title="Teste de WebHook">
              <WebhookTest />
            </Card>

          </div>
        );

      case 'org-sequence':
        return (
          <div>
            <Title level={2}>Sequência de NR1</Title>
            <OrgSequence />
          </div>
        );

      case 'label-templates':
        return (
          <div>
            <Title level={2}>Modelos de Etiqueta</Title>
            <LabelTemplatesManagement />
          </div>
        );

      case 'ws-print':
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Title level={2}>WebSocket de impressão (WS-Print)</Title>
              <Space>
                <Button icon={<ReloadOutlined />} onClick={refreshWsInfo} loading={wsLoading}>Atualizar</Button>
              </Space>
            </div>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} md={12}>
                <Card title="Status do Servidor" loading={wsLoading}>
                  {wsStatus ? (
                    <Space direction="vertical" size="small">
                      <div>
                        <Text strong>Servidor:</Text> <Tag color="blue">{wsStatus.server}</Tag>
                      </div>
                      <div>
                        <Text strong>Protocol:</Text> <Tag color={wsStatus.protocol === 'https' ? 'green' : 'geekblue'}>{wsStatus.protocol?.toUpperCase()}</Tag>
                      </div>
                      <div>
                        <Text strong>Porta:</Text> <Tag>{wsStatus.port}</Tag>
                      </div>
                      <div>
                        <Text strong>Conectado:</Text> <Tag color={wsStatus.conectado ? 'green' : 'red'}>{wsStatus.conectado ? 'SIM' : 'NíO'}</Tag>
                      </div>
                      <div>
                        <Text strong>Clientes Conectados:</Text> <Tag color={wsStatus.clientCount > 0 ? 'green' : 'red'}>{wsStatus.clientCount}</Tag>
                      </div>
                      <div>
                        <Text strong>Iniciado em:</Text> {new Date(wsStatus.startedAt).toLocaleString('pt-BR')}
                      </div>
                    </Space>
                  ) : (
                    <Text type="secondary">Sem dados de status</Text>
                  )}
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card title="Ações">
                  <Space>
                    <Button onClick={refreshWsInfo} type="primary" icon={<ReloadOutlined />} loading={wsLoading}>Atualizar dados</Button>
                  </Space>
                </Card>
              </Col>
            </Row>

            <Card title={`Clientes Conectados (${wsClients.length})`} loading={wsLoading}>
              <Table
                dataSource={wsClients}
                rowKey={(row) => row.uuid || `${row.ip}-${row.authorization}-${row.computer}`}
                pagination={{ pageSize: 10, showSizeChanger: true }}
                scroll={{ x: 'max-content' }}
                columns={[
                  { title: 'UUID', dataIndex: 'uuid', key: 'uuid' },
                  { title: 'Usuário', dataIndex: 'usuario', key: 'usuario' },
                  { title: 'Nome', dataIndex: 'nome', key: 'nome' },
                  { title: 'Empresa', dataIndex: 'empresa', key: 'empresa' },
                  { title: 'Dispositivo', dataIndex: 'computer', key: 'computer' },
                  { title: 'Versão', dataIndex: 'versao', key: 'versao' },
                  { title: 'Servidor', dataIndex: 'server', key: 'server' },
                  { title: 'Conectado', dataIndex: 'connected', key: 'conectado', render: (val) => (<Tag color={val ? 'green' : 'red'}>{val ? 'SIM' : 'NíO'}</Tag>) },
                  { title: 'Pareado', dataIndex: 'pareado', key: 'pareado' },
                  { title: 'IP', dataIndex: 'ip', key: 'ip' },
                ]}
              />
            </Card>
          </div>
        );

      case 'login-history':
        return (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Title level={2}>Histórico de Login</Title>
              <Space>
                <DatePicker.RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  placeholder={['Data inicial', 'Data final']}
                  format="DD/MM/YYYY"
                />
                <Button icon={<ReloadOutlined />} onClick={loadLoginHistory}>Atualizar</Button>
              </Space>
            </div>
            <Card>
              <Table
                dataSource={filteredLoginHistory}
                columns={loginHistoryColumns}
                rowKey="id"
                scroll={{ x: 'max-content' }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total) => `Total: ${total} registros`
                }}
              />
            </Card>
          </div>
        );

      default:
        return <div>Seção não encontrada</div>;
    }
  };

  const sidebarContent = (
    <Menu
      mode="inline"
      selectedKeys={[activeSection]}
      items={menuItems}
      onClick={({ key }) => {
        setActiveSection(key);
        setMobileDrawerVisible(false);
      }}
      style={{ border: 'none' }}
    />
  );

  return (
    <Layout className="admin-dashboard">
      <Navbar
        onMenuClick={() => setMobileDrawerVisible(true)}
      />

      {/* Botío toggle para sidebar em telas menores */}
      <Button
        type="primary"
        icon={<MenuOutlined />}
        onClick={() => setMobileDrawerVisible(true)}
        className="mobile-sidebar-toggle"
        style={{
          position: 'fixed',
          top: '40px',
          left: '16px',
          zIndex: 1000,
          display: 'none'
        }}
      />

      <Layout style={{ marginTop: 40 }}>
        {/* Sidebar Desktop */}
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          className="admin-sidebar desktop-sidebar"
          width={250}
          style={{
            position: 'fixed',
            height: 'calc(100vh - 40px)',
            left: 0,
            top: 40,
            zIndex: 100
          }}
        >
          <div className="sidebar-header">
            <Button
              type="text"
              icon={collapsed ? <MenuOutlined /> : <MenuOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ color: 'white', marginBottom: 16 }}
            />
            {!collapsed && (
              <div style={{ color: 'white', fontWeight: 'bold', marginLeft: 8 }}>
                Administração
              </div>
            )}
          </div>

          {sidebarContent}

          {!collapsed && (
            <div className="sidebar-footer">
            </div>
          )}
        </Sider>

        {/* Drawer Mobile */}
        <Drawer
          title="Administração"
          placement="left"
          onClose={() => setMobileDrawerVisible(false)}
          open={mobileDrawerVisible}
          className="mobile-drawer"
        >
          {sidebarContent}
          <Divider />
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button
              type="text"
              icon={<SettingOutlined />}
              onClick={toggleTheme}
              style={{ width: '100%', textAlign: 'left' }}
            >
              Tema: {currentTheme === 'dark' ? 'Escuro' : 'Claro'}
            </Button>
            <Button
              type="text"
              icon={<LogoutOutlined />}
              onClick={logout}
              style={{ width: '100%', textAlign: 'left' }}
            >
              Sair
            </Button>
          </Space>
        </Drawer>

        <Layout style={{ marginLeft: collapsed ? 80 : 250, transition: 'margin-left 0.2s' }}>
          <Content className="admin-content">
            {renderContent()}
          </Content>
        </Layout>
      </Layout>

      {/* Modal de Usuário */}
      <Modal
        title={editingUser ? 'Editar Usuário' : 'Novo Usuário'}
        open={userModalVisible}
        onCancel={() => {
          setUserModalVisible(false);
          setEditingUser(null);
          userForm.resetFields();
        }}
        footer={null}
        width={600}
        maskClosable={false}
        style={{ top: 0 }}
      >
        <Form
          form={userForm}
          layout="vertical"
          onFinish={handleUserSubmit}
          key={editingUser ? `edit-${editingUser.id}` : 'create'}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="codigo"
                label="Código"
                rules={[{ required: true, message: 'Código é obrigatório' }]}
              >
                <Input placeholder="Ex: USR001" disabled={!!editingUser} />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="name"
                label="Nome"
                rules={[{ required: true, message: 'Nome é obrigatório' }]}
              >
                <Input placeholder="Nome completo do usuário" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="password"
                label="Senha"
                rules={[
                  { required: !editingUser, message: 'Senha é obrigatória' },
                  { min: 6, message: 'Senha deve ter pelo menos 6 caracteres' }
                ]}
              >
                <Input.Password placeholder={editingUser ? "Deixe em branco para manter a senha atual" : "Digite a senha"} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="roles"
                label="Roles"
                rules={[{ required: true, message: 'Pelo menos um role é obrigatório' }]}
              >
                <Select mode="multiple" placeholder="Selecione os roles">
                  <Option value="inadmin">Admin</Option>
                  <Option value="inmanager">Manager</Option>
                  <Option value="inoperacional">Operacional</Option>
                  <Option value="inexpedicao">Expedição</Option>
                  <Option value="inonboard">Onboard</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="assignedBranchId"
                label="Filial"
                rules={[{ required: true, message: 'Filial é obrigatória' }]}
              >
                <Select placeholder="Selecione a filial">
                  {branches.map(branch => (
                    <Option key={branch.id} value={branch.id}>{branch.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="active" label="Ativo" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingUser ? 'Atualizar' : 'Criar'}
              </Button>
              <Button onClick={() => {
                setUserModalVisible(false);
                setEditingUser(null);
                userForm.resetFields();
              }}>
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Filial */}
      <Modal
        title={editingBranch ? 'Editar Filial' : 'Nova Filial'}
        open={branchModalVisible}
        onCancel={() => {
          setBranchModalVisible(false);
          setEditingBranch(null);
          branchForm.resetFields();
        }}
        footer={null}
        width={600}
        maskClosable={false}
      >
        <Form
          form={branchForm}
          layout="vertical"
          onFinish={handleBranchSubmit}
          key={editingBranch ? `edit-${editingBranch.id}` : 'create'}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="codigo"
                label="Código"
                tooltip="Opcional. Até 10 caracteres."
              >
                <Input maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="acronym"
                label="Sigla"
                rules={[{ required: true, message: 'Sigla é obrigatória' }]}
              >
                <Input maxLength={6} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="active" label="Ativa" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="name"
            label="Nome"
            rules={[{ required: true, message: 'Nome é obrigatório' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingBranch ? 'Atualizar' : 'Criar'}
              </Button>
              <Button onClick={() => {
                setBranchModalVisible(false);
                setEditingBranch(null);
                branchForm.resetFields();
              }}>
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default AdminDashboard;